import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { ChangeEvent, useEffect, useMemo } from 'react'
import { useUpdateEffect } from 'usehooks-ts'
import { TableContextMenu, debounce } from '~/shared/lib'
import { TableContainer } from '~/shared/ui/table'
import { getColumns } from './lib/config'
import { useStore } from '~/app/store'
import GoToTop from '~/features/scroll-to-top'
import { Input } from '~/shared/ui/input'
import { ListUser } from '~/shared/api/services/users/types'
import { usePaginationTrigger } from '~/shared/lib/hooks/use-pagination-trigger'

export const UsersTableWidget = observer(() => {
  const { t } = useTranslation()
  const columns = useMemo(() => getColumns(t), [t])
  const navigate = useNavigate()
  const { usersStore } = useStore()
  const {
    getUsersById,
    usersList,
    q,
    getUsers,
    setQ,
    isMoreLoading,
    isListLoading,
    isEndUserList,
  } = usersStore

  const { isVisible, anchor } = usePaginationTrigger([
    isListLoading,
    isMoreLoading,
  ])

  const handleRowClick = (row: object) => {
    if ('id' in row) {
      getUsersById(row.id as string)
      navigate(`/users/${row.id}/edit`)
    }
  }

  const debouncedQuery = useMemo(() => {
    return debounce((arg) => {
      const action = arg as 'q' | 'loadMore' | 'init'
      return getUsers(action)
    }, 1000)
  }, [getUsers])

  useEffect(() => {
    if (!usersList.length && !q.trim()) {
      getUsers('init')
    }
  }, [getUsers, q, usersList.length])

  useEffect(() => {
    if (q) {
      debouncedQuery('q')
    }
  }, [q, debouncedQuery])

  const contextMenu: TableContextMenu<ListUser>[] = [
    {
      text: t('edit'),
      handler: (row) => {
        getUsersById(row.id as string)
        navigate(`/users/${row.id}/edit`)
      },
    },
  ]

  useUpdateEffect(() => {
    if (isVisible && usersList.length && !isEndUserList && !isListLoading) {
      getUsers('loadMore')
    }
  }, [isVisible, usersList.length, isEndUserList, isListLoading])

  return (
    <>
      <Input
        id="usersQ"
        filterIcon={!q.length}
        size="small"
        value={q}
        crossRemove={q.length}
        labelType="floating"
        label={`${t('name')},  ${t('email')}`}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          setQ(e.target.value || '')
          if (!e.target.value) {
            debouncedQuery('init')
          }
        }}
        className="rounded-md border border-gray-300"
      />
      <div className="mt-10">
        <TableContainer
          fixedWidth
          columns={columns}
          data={usersList}
          loadMore={isMoreLoading}
          isLoading={isListLoading}
          onRowClick={(row) => handleRowClick(row)}
          contextMenu={contextMenu}
        />
        {anchor}
      </div>
      <GoToTop />
    </>
  )
})
